import React from 'react';
import { useLocation } from 'react-router-dom';
import {
  Button as MUIButton,
  useTheme,
  useMediaQuery,
  Box,
  Divider as MUIDivider,
  Typography,
} from '@mui/material';
import chrLogo from '@chr/autoStyle/dist/img/CHR/CHR_LogoTM_Horz_2C_RGB.png';

export interface FooterProps {
  /**
   * Optional disclaimer text to show below copyright text.
   * @remarks Supports the following newline tokens: `'\n'`, `'&#10;'`
   */
  disclaimerText?: string;
}

const year = new Date().getUTCFullYear();

const links: {
  [key in 'privacyNotice' | 'privacyRights' | 'termsOfUse']: {
    key: string;
    buttonText: string;
    href: string;
  };
} = {
  privacyNotice: {
    key: 'privacy-notice',
    buttonText: 'Global Privacy Notice',
    href: 'https://www.chrobinson.com/en-us/privacy-notice/',
  },
  privacyRights: {
    key: 'privacy-rights',
    buttonText: 'Your Privacy Rights',
    href: 'https://www.chrobinson.com/en-us/your-privacy-rights/',
  },
  termsOfUse: {
    key: 'terms',
    buttonText: 'Terms of Use',
    href: 'https://www.chrobinson.com/en-us/terms-of-use/',
  },
};

export const Footer = (props: FooterProps) => {
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down('sm'));
  const isSmOrSmaller = useMediaQuery(theme.breakpoints.down('md'));
  const location = useLocation();
  const isHome = location.pathname === '/';

  const linksToRender = [
    {
      ...links.privacyNotice,
    },
    {
      ...links.privacyRights,
    },
    {
      ...links.termsOfUse,
    },
  ];

  let legalese = `© 1996-${year} C.H. Robinson Worldwide, Inc. All rights reserved.`;
  if (props.disclaimerText) {
    legalese += '\n' + props.disclaimerText.replace(/&#10;|\\n/g, '\n');
  }

  return (
    <Box
      padding={'24px 48px'}
      width={'100%'}
      sx={
        isHome
          ? {
              color: 'white',
              backgroundColor: 'transparent',
            }
          : { backgroundColor: 'white' }
      }
    >
      {!isSmOrSmaller && (
        <Box mb={2}>
          <img
            src={isHome ? './svg/home-logo.svg' : chrLogo}
            alt="C.H. Robinson Logo"
            title="C.H. Robinson Logo"
            width="174"
            height="40"
          />
        </Box>
      )}
      <Box
        display="flex"
        flexDirection={isSmOrSmaller ? 'column' : 'row'}
        justifyContent="space-between"
        alignItems={isSmOrSmaller ? 'center' : 'last baseline'}
        gap={isSmOrSmaller ? 2 : 3}
      >
        <Typography
          variant="caption"
          align={isSmOrSmaller ? 'center' : undefined}
          whiteSpace="pre-line"
        >
          {legalese}
        </Typography>

        <Box
          display="flex"
          flexDirection={isXs ? 'column' : 'row'}
          flexWrap="wrap"
          justifyContent="center"
          gap={isXs ? 0: 1}
        >
          {linksToRender.map((link) => {
            return (
              <React.Fragment key={link.key}>
                <MUIButton
                  sx={{
                    ...(isHome && {
                      color: 'white',
                      '&:hover': { color: 'white' },
                    }),
                  }}
                  variant="text"
                  href={link.href}
                >
                  {link.buttonText}
                </MUIButton>
                <MUIDivider orientation="vertical" variant="middle" flexItem />
              </React.Fragment>
            );
          })}
          <MUIButton
            data-testid="cookie-preferences"
            id="teconsent"
            sx={{
              width: 'auto',
              '& a': {
                textDecoration: 'none',
                ...(isHome && {
                  color: 'white',
                  '&:hover': { color: 'white' },
                }),
              },
            }}
          />
        </Box>
      </Box>
      <Box id="consent_blackbar" />
    </Box>
  );
};
